import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import { Container } from "@pixi/react";
import HoldAndWinTensionOverlayController from "./HoldAndWinTensionOverlayController";
import { safeInit } from "../../utilities";

/**
 * Hold and Win Tension Overlay Component
 * Manages tension animation for a single symbol position
 * Completely independent from multiplier animations
 */
const HoldAndWinTensionOverlay = forwardRef(
  ({ reelIndex, symbolIndex, symbolWidth, symbolHeight }, ref) => {
    const tensionControllerRef = useRef(null);
    const tensionContainerRef = useRef(null);

    // Memoize container name
    const containerName = useMemo(
      () => `hold_and_win_tension_${reelIndex}_${symbolIndex}`,
      [reelIndex, symbolIndex]
    );

    // Initialize controller when assets are ready
    useEffect(() => {
      if (tensionContainerRef.current && !tensionControllerRef.current) {
        safeInit(() => {
          tensionControllerRef.current = new HoldAndWinTensionOverlayController(
            tensionContainerRef.current,
            reelIndex,
            symbolIndex,
            symbolWidth,
            symbolHeight
          );
        });
      }

      return () => {
        if (tensionControllerRef.current) {
          tensionControllerRef.current.cleanup?.();
          tensionControllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle symbol size changes for responsive design
    useEffect(() => {
      if (tensionControllerRef.current) {
        tensionControllerRef.current.resizeTension(symbolWidth, symbolHeight);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbolHeight]);

    // Memoize controller methods
    const playTensionIn = useCallback(() => {
      tensionControllerRef.current?.playTensionIn();
    }, []);

    const playTensionOut = useCallback(() => {
      tensionControllerRef.current?.playTensionOut();
    }, []);

    const hideTension = useCallback(() => {
      tensionControllerRef.current?.hideTension();
    }, []);

    const forceStopTension = useCallback(() => {
      tensionControllerRef.current?.forceStopTension();
    }, []);

    // Expose controller methods to parent components
    useImperativeHandle(ref, () => ({
      playTensionIn,
      playTensionOut,
      hideTension,
      forceStopTension,
    }), [playTensionIn, playTensionOut, hideTension, forceStopTension]);

    return (
      <Container
        ref={tensionContainerRef}
        name={containerName}
        sortableChildren
      />
    );
  }
);

export default HoldAndWinTensionOverlay;
